import {
    dropRight,
    includes,
    isArrayLikeObject,
    isEmpty,
    isNull,
    isObjectLike,
    isUndefined,
    filter,
    findIndex,
    find,
    flatten,
    forEach,
    map,
    orderBy,
    uniqBy,
} from 'lodash';

import { resetOptionFromProtectionItem } from 'commons-vehicle/ModelYear/services/OptionService/OptionService.js';
import { DATA_TYPES } from 'commons-vehicle/constants/dataTypes.js';
import {
    OPTION,
    ACCESSORY,
    DEALER_INVENTORY,
    DEALER_DISCOUNT,
    EXTERIOR_COLOUR,
    INTERIOR_TRIM,
} from 'commons-vehicle/Quote/constants/modelTrimItemCategories.js';

import QuoteItemCategoryModel from 'commons-vehicle/Quote/models/QuoteItemCategoryModel.js';

const { PROTECTION, FEATURE, REBATE } = DATA_TYPES;

function splitItemsAndPackages(quoteItemCategories, quoteItemsPackages, quoteItems) {
    return filter(
        map(quoteItemCategories, (itemCategory) => {
            const itemsPackages = orderBy(
                filter(quoteItemsPackages, (item) => {
                    return item.categoryID === itemCategory.id;
                }),
                ['keyOrder']
            );
            const items = orderBy(
                filter(quoteItems, (item) => {
                    return item.categoryID === itemCategory.id;
                }),
                ['keyOrder']
            );

            const noItems = isEmpty(items) && isEmpty(itemsPackages);
            return itemCategory
                .set('visible', !noItems)
                .set('itemsPackages', itemsPackages)
                .set('items', items);
        }),
        'visible'
    );
}

function getItemCategoryFromType(item, categoryId) {
    const notColour = item.typeID !== FEATURE.TYPES.EXTERIOR_COLOUR && item.typeID !== FEATURE.TYPES.INTERIOR_TRIM;

    if (item.typeID === FEATURE.TYPES.EXTERIOR_COLOUR && categoryId === EXTERIOR_COLOUR) {
        return EXTERIOR_COLOUR;
    } else if (item.typeID === FEATURE.TYPES.INTERIOR_TRIM && categoryId === INTERIOR_TRIM) {
        return INTERIOR_TRIM;
    } else if (notColour && item.isInstalledAtDealership && categoryId === ACCESSORY) {
        return ACCESSORY;
    } else if (notColour && !item.isInstalledAtDealership && categoryId === OPTION) {
        return OPTION;
    }
    return 0;
}

function splitModelTrimItemsAndPackages(quoteItemCategories, modelItemsPackages, modelItems) {
    const parentCodeInclusions = {};
    forEach(modelItems, (item) => {
        if (!parentCodeInclusions[item.parentCode]) {
            parentCodeInclusions[item.parentCode] = [];
        }
        parentCodeInclusions[item.parentCode].push(item.selectionCode);
    });
    const newModelItemsPackages = map(modelItemsPackages, (itemsPackage) => {
        return parentCodeInclusions[itemsPackage.code]
            ? itemsPackage.set('includedCodes', parentCodeInclusions[itemsPackage.code])
            : itemsPackage;
    });

    return map(quoteItemCategories, (itemCategory) => {
        const itemsPackages = orderBy(
            filter(newModelItemsPackages, (item) => {
                return getItemCategoryFromType(item, itemCategory.id) > 0;
            }),
            ['description']
        );
        const items = orderBy(
            filter(modelItems, (item) => {
                return getItemCategoryFromType(item, itemCategory.id) > 0;
            }),
            ['description']
        );

        const noItems = isEmpty(items) && isEmpty(itemsPackages);
        return itemCategory
            .set('visible', !noItems && itemCategory.id <= ACCESSORY)
            .set('itemsPackages', itemsPackages)
            .set('items', items);
    });
}

function extractCategoriesWebUrls(quoteItemCategories) {
    return map(quoteItemCategories, (itemCategory) => {
        const webUrls = uniqBy(itemCategory.itemsPackages, 'webUrl');
        if (!itemCategory.webUrl && webUrls.length === 1) {
            return itemCategory
                .set('webUrl', webUrls[0].webUrl)
                .set(
                    'itemsPackages',
                    map(itemCategory.itemsPackages, (itemsPackage) => itemsPackage.set('webUrl', ''))
                );
        }
        return itemCategory;
    });
}

function extractCategoriesNotes(quoteItemCategories) {
    return map(quoteItemCategories, (itemCategory) => {
        const typeNotes = uniqBy(itemCategory.itemsPackages, 'note');
        if (!itemCategory.note && typeNotes.length === 1) {
            return itemCategory
                .set('note', typeNotes[0].note)
                .set('itemsPackages', map(itemCategory.itemsPackages, (itemsPackage) => itemsPackage.set('note', '')));
        }
        return itemCategory;
    });
}

function unselectItemsAndPackages(quoteItemCategory) {
    return quoteItemCategory
        .set(
            'items',
            map(quoteItemCategory.items, (item) => {
                return item.set('selected', false);
            })
        )
        .set(
            'itemsPackages',
            map(quoteItemCategory.itemsPackages, (item) => {
                return item.set('selected', false);
            })
        );
}

function resetItemsAndPackagesAmountsFromOptions(quoteItemCategory, options) {
    const itemsPackages = map(quoteItemCategory.itemsPackages, (item) => {
        const itemOption = find(options, (option) => {
            return option.selectionCode === item.selectionCode;
        });
        return item
            .set('retail', itemOption ? itemOption.retail : item.retail)
            .set('residualizingAmount', itemOption ? itemOption.residualizingAmount : item.residualizingAmount);
    });

    const items = map(quoteItemCategory.items, (item) => {
        const itemOption = find(options, (option) => {
            return option.selectionCode === item.selectionCode;
        });
        return item
            .set('retail', itemOption ? itemOption.retail : item.retail)
            .set('residualizingAmount', itemOption ? itemOption.residualizingAmount : item.residualizingAmount);
    });

    return quoteItemCategory.set('itemsPackages', itemsPackages).set('items', items);
}

function selectItemsAndPackagesFromCodes(quoteItemCategory, itemsPackageSelectionCodes) {
    const itemsPackages = map(quoteItemCategory.itemsPackages, (item) => {
        if (
            (quoteItemCategory.id === DEALER_INVENTORY && quoteItemCategory.dataType === FEATURE.DATA_TYPE_ID) ||
            (quoteItemCategory.id === DEALER_DISCOUNT && quoteItemCategory.dataType === REBATE.DATA_TYPE_ID)
        ) {
            return item.set('selected', true);
        }
        return item.set('selected', includes(itemsPackageSelectionCodes, item.selectionCode));
    });

    const selectedItemsPackages = filter(itemsPackages, 'selected');
    const includedCodes = flatten(
        map(selectedItemsPackages, (itemsPackage) => {
            return itemsPackage.includedCodes;
        })
    );

    const items = map(quoteItemCategory.items, (item) => {
        return item.set(
            'selected',
            includes(includedCodes, item.selectionCode) || includes(itemsPackageSelectionCodes, item.selectionCode)
        );
    });

    return quoteItemCategory.set('itemsPackages', itemsPackages).set('items', items);
}

function disableCategory(quoteItemCategory) {
    const unselectedItemCategory = unselectItemsAndPackages(quoteItemCategory);
    return unselectedItemCategory
        .set('selected', false)
        .set('enabled', false)
        .set('declined', false);
}

function setCategoryDone(quoteItemCategory, declineProtections) {
    let quoteItemCatSelected = quoteItemCategory;
    if (declineProtections) {
        quoteItemCatSelected = unselectItemsAndPackages(quoteItemCategory);
    }

    const declined =
        declineProtections ||
        (quoteItemCatSelected.getSelectedItemsCount() === 0 &&
            quoteItemCatSelected.getSelectedItemsPackagesCount() === 0);

    return quoteItemCatSelected.set('selected', false).set('declined', declined);
}

function setSelectedItemsAndPackages(quoteItemCategory, selectedOptions) {
    const selectedOptionsCodes = map(selectedOptions, (selectedOption) => {
        return selectedOption.selectionCode;
    });
    let newQuoteItemCategory = unselectItemsAndPackages(quoteItemCategory);
    newQuoteItemCategory = resetItemsAndPackagesAmountsFromOptions(newQuoteItemCategory, selectedOptions);
    return selectItemsAndPackagesFromCodes(newQuoteItemCategory, selectedOptionsCodes);
}

export function resetQuoteItemCategoriesFromQuoteProtections(quoteItemCategories, quoteProtections) {
    if (!isArrayLikeObject(quoteItemCategories) || isEmpty(quoteItemCategories)) {
        return [];
    }
    if (!isArrayLikeObject(quoteProtections) || isEmpty(quoteProtections)) {
        return quoteItemCategories;
    }

    const protectionPackages = [];
    const protections = [];

    forEach(quoteProtections, (protection) => {
        if (protection.dataType === PROTECTION.DATA_TYPE_ID) {
            if (
                !isEmpty(protection.identificationType) &&
                (protection.identificationType.isGroup || protection.categoryID === 6)
            ) {
                protectionPackages.push(resetOptionFromProtectionItem(protection));
            } else {
                protections.push(resetOptionFromProtectionItem(protection));
            }
        }
    });

    const quoteItemCatSplitItems = splitItemsAndPackages(quoteItemCategories, protectionPackages, protections);
    const quoteItemCatUrls = extractCategoriesWebUrls(quoteItemCatSplitItems);
    const quoteItemCatNotes = extractCategoriesNotes(quoteItemCatUrls);

    return map(quoteItemCatNotes, (protectionCategory, index) => {
        return protectionCategory.set('selected', index === 0).set('enabled', index === 0);
    });
}

export function resetQuoteItemCategoryFromCategory(quoteItemCategory, dataType, category) {
    if (!isObjectLike(category) || isUndefined(category.categoryID)) {
        return quoteItemCategory;
    }
    return quoteItemCategory
        .set('id', category.categoryID)
        .set(
            'singlePackageSelection',
            category.categoryID !== PROTECTION.CATEGORIES.INSURANCE &&
                category.categoryID !== PROTECTION.CATEGORIES.PROTECTION
        )
        .set('description', isEmpty(category.description) ? '' : category.description.descriptions[0])
        .set('note', isEmpty(category.description) ? '' : category.description.notes[0])
        .set('dataType', dataType)
        .set('order', category.rank);
}

export function resetQuoteItemCategoriesFromModelTrimOptions(modelOptions) {
    if (!isArrayLikeObject(modelOptions) || isEmpty(modelOptions)) {
        return [];
    }

    const quoteItemCategories = [
        new QuoteItemCategoryModel({
            id: OPTION,
            dataType: FEATURE.DATA_TYPE_ID,
            singlePackageSelection: false,
            order: OPTION,
        }),
        new QuoteItemCategoryModel({
            id: ACCESSORY,
            dataType: FEATURE.DATA_TYPE_ID,
            singlePackageSelection: false,
            order: ACCESSORY,
        }),
        new QuoteItemCategoryModel({
            id: DEALER_INVENTORY,
            dataType: FEATURE.DATA_TYPE_ID,
            singlePackageSelection: false,
            order: DEALER_INVENTORY,
        }),
        new QuoteItemCategoryModel({
            id: DEALER_DISCOUNT,
            dataType: REBATE.DATA_TYPE_ID,
            singlePackageSelection: false,
            order: DEALER_DISCOUNT,
        }),
        new QuoteItemCategoryModel({
            id: EXTERIOR_COLOUR,
            dataType: FEATURE.DATA_TYPE_ID,
            singlePackageSelection: true,
            order: EXTERIOR_COLOUR,
        }),
        new QuoteItemCategoryModel({
            id: INTERIOR_TRIM,
            dataType: FEATURE.DATA_TYPE_ID,
            singlePackageSelection: true,
            order: INTERIOR_TRIM,
        }),
    ];

    const optionsAccessoriesPackages = [];
    const optionsAccessories = [];

    forEach(modelOptions, (optionAccessory) => {
        if (isEmpty(optionAccessory.parentCode)) {
            optionsAccessoriesPackages.push(optionAccessory);
        } else {
            optionsAccessories.push(optionAccessory);
        }
    });

    const quoteItemCatSplitItems = splitModelTrimItemsAndPackages(
        quoteItemCategories,
        optionsAccessoriesPackages,
        optionsAccessories
    );
    return map(quoteItemCatSplitItems, (protectionCategory, index) => {
        return protectionCategory.set('selected', index === 0).set('enabled', index === 0);
    });
}

export function selectQuoteItemCategory(quoteItemCategories, quoteItemCategory, declineProtections) {
    if (!isArrayLikeObject(quoteItemCategories) || isEmpty(quoteItemCategories)) {
        return [];
    }
    if ((!isObjectLike(quoteItemCategory) || isUndefined(quoteItemCategory.id)) && !isNull(quoteItemCategory)) {
        return quoteItemCategories;
    }

    const categoriesIds = map(quoteItemCategories, (itemCategory) => itemCategory.id);
    const categoriesCount = categoriesIds.length;
    let currentIndex = categoriesCount;
    if (quoteItemCategory !== null) {
        currentIndex = findIndex(quoteItemCategories, (itemCategory) => {
            return itemCategory.id === quoteItemCategory.id;
        });
    }
    const previousIds = dropRight(categoriesIds, categoriesCount - currentIndex);

    return map(quoteItemCategories, (itemCategory) => {
        if (includes(previousIds, itemCategory.id)) {
            return itemCategory.selected ? setCategoryDone(itemCategory, declineProtections) : itemCategory;
        } else if (itemCategory.id === quoteItemCategory.id) {
            return itemCategory
                .set('selected', true)
                .set('enabled', true)
                .set('declined', false);
        }
        return disableCategory(itemCategory);
    });
}

export function selectNextPreviousQuoteItemCategory(
    quoteItemCategories,
    quoteItemCategory,
    previous,
    declineProtections
) {
    if (!isArrayLikeObject(quoteItemCategories) || isEmpty(quoteItemCategories)) {
        return [];
    }

    const nextIndex =
        findIndex(quoteItemCategories, (itemCategory) => {
            return itemCategory.id === quoteItemCategory.id;
        }) + (previous ? -1 : 1);

    if (nextIndex < 0) {
        return selectQuoteItemCategory(quoteItemCategories, quoteItemCategory, declineProtections);
    } else if (nextIndex >= quoteItemCategories.length) {
        return selectQuoteItemCategory(quoteItemCategories, null, declineProtections);
    }
    return selectQuoteItemCategory(quoteItemCategories, quoteItemCategories[nextIndex], declineProtections);
}

export function selectQuoteItemPackage(quoteItemCategories, quoteItemCategory, itemsPackage) {
    if (!isArrayLikeObject(quoteItemCategories) || isEmpty(quoteItemCategories)) {
        return [];
    }
    if (
        !isObjectLike(quoteItemCategory) ||
        isUndefined(quoteItemCategory.id) ||
        !isObjectLike(itemsPackage) ||
        isUndefined(itemsPackage.selectionCode)
    ) {
        return quoteItemCategories;
    }

    const itemsPackages = map(quoteItemCategory.itemsPackages, (item) => {
        if (quoteItemCategory.singlePackageSelection) {
            const selected = item.selectionCode === itemsPackage.selectionCode && !itemsPackage.selected;
            return item.set('selected', selected).set('userSelected', selected);
        }
        const multiSelected =
            (item.selectionCode === itemsPackage.selectionCode && !itemsPackage.selected) ||
            (item.selectionCode !== itemsPackage.selectionCode && item.selected);
        return item.set('selected', multiSelected).set('userSelected', multiSelected);
    });

    const items = map(quoteItemCategory.items, (item) => {
        if (quoteItemCategory.singlePackageSelection) {
            const selected = !itemsPackage.selected && includes(itemsPackage.includedCodes, item.selectionCode);
            return item.set('selected', selected).set('userSelected', selected);
        }
        const multiSelected =
            (includes(itemsPackage.includedCodes, item.selectionCode) && !itemsPackage.selected) ||
            (!includes(itemsPackage.includedCodes, item.selectionCode) && item.selected);
        return item.set('selected', multiSelected).set('userSelected', multiSelected);
    });

    const newQuoteItemCategory = quoteItemCategory.set('itemsPackages', itemsPackages).set('items', items);

    return map(quoteItemCategories, (itemCategory) => {
        return itemCategory.id === newQuoteItemCategory.id ? newQuoteItemCategory : itemCategory;
    });
}

export function resetQuoteItemCategoriesFromOptionsValidation(quoteItemCategories, selectedOptions) {
    if (!isArrayLikeObject(quoteItemCategories) || isEmpty(quoteItemCategories)) {
        return [];
    }
    if (!isArrayLikeObject(selectedOptions) || isEmpty(selectedOptions)) {
        return quoteItemCategories;
    }

    return map(quoteItemCategories, (itemCategory) => {
        return setSelectedItemsAndPackages(itemCategory, selectedOptions);
    });
}

export default {
    resetQuoteItemCategoriesFromQuoteProtections,
    resetQuoteItemCategoryFromCategory,
    resetQuoteItemCategoriesFromModelTrimOptions,
    selectNextPreviousQuoteItemCategory,
    selectQuoteItemCategory,
    selectQuoteItemPackage,
    resetQuoteItemCategoriesFromOptionsValidation,
};



// WEBPACK FOOTER //
// ./src/Quote/services/QuoteItemCategoryService/QuoteItemCategoryService.js